import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Tooltip,
  CircularProgress,
  alpha,
  useTheme,
} from "@mui/material";
import {
  ArrowLeft,
  Plus,
  Trash2,
  PenLine,
  Users,
  UserCheck,
  Reply,
  XCircle,
  TrendingUp,
  Percent,
  Eye,
  UserPlus,
  Heart,
  ThumbsUp,
  MessageSquare,
  Mail,
  UserMinus,
  Clock,
  GitBranch,
  ChevronDown,
  ChevronRight,
  Building2,
} from "lucide-react";
import {
  fetchLICampaign,
  fetchLICampaignStats,
  fetchLICampaignLeads,
  fetchLISteps,
  createLIStep,
  updateLIStep,
  deleteLIStep,
  addLeadsToLICampaign,
  fetchLILeads,
} from "../../../../../../redux/actions/linkedinOutreachAction";

const STATUS_COLORS = {
  draft: "default",
  active: "success",
  paused: "warning",
  completed: "info",
};

const LEAD_STATUS_COLORS = {
  pending: "default",
  in_progress: "info",
  connected: "success",
  replied: "primary",
  failed: "error",
  completed: "success",
  skipped: "default",
};

const ACTION_TYPES = [
  { value: "view_profile", label: "View Profile", icon: Eye },
  { value: "follow", label: "Follow", icon: UserPlus },
  { value: "like_post", label: "Like Post", icon: ThumbsUp },
  { value: "connect", label: "Send Connection", icon: UserCheck },
  { value: "message", label: "Send Message", icon: MessageSquare },
  { value: "inmail", label: "Send InMail", icon: Mail },
  { value: "invite_to_follow_company", label: "Invite to Follow Page", icon: Building2 },
  { value: "withdraw_invite", label: "Withdraw Invite", icon: UserMinus },
  { value: "wait", label: "Wait / Delay", icon: Clock },
];

const CONDITION_TYPES = [
  { value: "", label: "None" },
  { value: "if_connected", label: "If Connected" },
  { value: "if_replied", label: "If Replied" },
  { value: "if_not_replied", label: "If Not Replied" },
];

const INITIAL_STEP_FORM = {
  step_order: 1,
  action_type: "view_profile",
  delay_hours: 0,
  delay_jitter_pct: 25,
  connection_note: "",
  message_template: "",
  condition_type: "",
  condition_timeout_hours: 24,
  on_true_goto_step: "",
  on_false_goto_step: "",
};

const getActionConfig = (actionType) =>
  ACTION_TYPES.find((a) => a.value === actionType) || {
    label: actionType,
    icon: Clock,
  };

/* ──────────── Stat Card ──────────── */
const StatCard = ({ icon: Icon, label, value, suffix, color }) => {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: "12px",
        border: `1px solid ${theme.palette.divider}`,
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
        flex: 1,
        minWidth: 130,
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: "10px",
          bgcolor: alpha(color, 0.1),
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon size={18} color={color} />
      </Box>
      <Box>
        <Typography
          variant="caption"
          sx={{
            color: theme.palette.text.secondary,
            fontWeight: 500,
            lineHeight: 1.2,
          }}
        >
          {label}
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.1rem" }}>
          {value ?? "-"}
          {suffix && (
            <Typography
              component="span"
              variant="body2"
              sx={{ color: theme.palette.text.secondary, ml: 0.3 }}
            >
              {suffix}
            </Typography>
          )}
        </Typography>
      </Box>
    </Paper>
  );
};

/* ──────────── Per-step Action Stats ──────────── */
const ActionStepStats = ({ stepStats = [] }) => {
  const theme = useTheme();

  if (!stepStats || stepStats.length === 0) return null;

  // Hide wait steps — they're not real actions
  const actionSteps = stepStats.filter((s) => s.action_type !== "wait");
  if (actionSteps.length === 0) return null;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 2.5,
        borderRadius: "12px",
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{ fontWeight: 700, mb: 1.5, color: theme.palette.text.primary }}
      >
        Action Performance
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
          gap: 1.5,
        }}
      >
        {actionSteps.map((s) => {
          const cfg = getActionConfig(s.action_type);
          const Icon = cfg.icon;
          const total = (s.success || 0) + (s.skipped || 0) + (s.failed || 0);
          return (
            <Box
              key={s.step_id}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.75,
                p: 1.5,
                borderRadius: "10px",
                bgcolor: alpha(theme.palette.primary.main, 0.04),
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "8px",
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Icon size={14} color={theme.palette.primary.main} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}
                  >
                    Step {s.step_order}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      lineHeight: 1.2,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {cfg.label}
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  gap: 1.5,
                  alignItems: "baseline",
                  flexWrap: "wrap",
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography
                    variant="caption"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    Success
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, color: "#10b981" }}
                  >
                    {s.success || 0}
                  </Typography>
                </Box>
                {s.skipped > 0 && (
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography
                      variant="caption"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      Skipped
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700, color: "#f59e0b" }}
                    >
                      {s.skipped}
                    </Typography>
                  </Box>
                )}
                {s.failed > 0 && (
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography
                      variant="caption"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      Failed
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700, color: "#ef4444" }}
                    >
                      {s.failed}
                    </Typography>
                  </Box>
                )}
                {s.pending > 0 && (
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography
                      variant="caption"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      Pending
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700, color: theme.palette.text.primary }}
                    >
                      {s.pending}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
};

const CampaignDetail = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { campaignId } = useParams();

  const { currentCampaign, campaignStats, campaignLeads, steps, leads, loading } =
    useSelector((state) => state.linkedinOutreach);

  const [activeTab, setActiveTab] = useState(0);
  const [leadsPage, setLeadsPage] = useState(0);

  // Step dialog state
  const [stepDialogOpen, setStepDialogOpen] = useState(false);
  const [editingStep, setEditingStep] = useState(null);
  const [stepForm, setStepForm] = useState(INITIAL_STEP_FORM);
  const [stepSubmitting, setStepSubmitting] = useState(false);

  // Add leads dialog state
  const [addLeadsOpen, setAddLeadsOpen] = useState(false);
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);
  const [addingLeads, setAddingLeads] = useState(false);

  // Delete step confirmation
  const [deleteStepTarget, setDeleteStepTarget] = useState(null);

  useEffect(() => {
    if (campaignId) {
      dispatch(fetchLICampaign(campaignId));
      dispatch(fetchLICampaignStats(campaignId));
      dispatch(fetchLISteps(campaignId));
    }
  }, [dispatch, campaignId]);

  useEffect(() => {
    if (campaignId && activeTab === 1) {
      dispatch(
        fetchLICampaignLeads(campaignId, { page: leadsPage + 1 })
      );
    }
  }, [dispatch, campaignId, activeTab, leadsPage]);

  useEffect(() => {
    if (addLeadsOpen) {
      dispatch(fetchLILeads({ status: "validated", not_in_campaign: campaignId, page_size: 100 }));
    }
  }, [dispatch, addLeadsOpen, campaignId]);

  const campaign = currentCampaign;
  const stats = campaignStats;
  const sortedSteps = [...(steps || [])].sort(
    (a, b) => a.step_order - b.step_order
  );
  const leadItems = campaignLeads?.items || [];
  const leadTotal = campaignLeads?.total || 0;
  const availableLeads = leads?.items || [];

  // ── Step handlers ──
  const handleOpenStepCreate = () => {
    setEditingStep(null);
    setStepForm({
      ...INITIAL_STEP_FORM,
      step_order: sortedSteps.length + 1,
    });
    setStepDialogOpen(true);
  };

  const handleOpenStepEdit = (step) => {
    setEditingStep(step);
    setStepForm({
      step_order: step.step_order || 1,
      action_type: step.action_type || "view_profile",
      delay_hours: step.delay_hours ?? 0,
      delay_jitter_pct: step.delay_jitter_pct ?? 25,
      connection_note: step.connection_note || "",
      message_template: step.message_template || "",
      condition_type: step.condition_type || "",
      condition_timeout_hours: step.condition_timeout_hours ?? 24,
      on_true_goto_step: step.on_true_goto_step ?? "",
      on_false_goto_step: step.on_false_goto_step ?? "",
    });
    setStepDialogOpen(true);
  };

  const handleCloseStepDialog = () => {
    setStepDialogOpen(false);
    setEditingStep(null);
    setStepForm(INITIAL_STEP_FORM);
  };

  const handleSaveStep = async () => {
    setStepSubmitting(true);
    try {
      const payload = { ...stepForm };
      if (!payload.condition_type) {
        delete payload.condition_type;
        delete payload.condition_timeout_hours;
        delete payload.on_true_goto_step;
        delete payload.on_false_goto_step;
      }
      if (payload.action_type !== "connect") {
        delete payload.connection_note;
      }
      if (!["message", "inmail"].includes(payload.action_type)) {
        delete payload.message_template;
      }
      if (payload.on_true_goto_step === "") delete payload.on_true_goto_step;
      if (payload.on_false_goto_step === "") delete payload.on_false_goto_step;

      if (editingStep) {
        await dispatch(updateLIStep(campaignId, editingStep.id, payload));
      } else {
        await dispatch(createLIStep(campaignId, payload));
      }
      handleCloseStepDialog();
    } finally {
      setStepSubmitting(false);
    }
  };

  const handleDeleteStep = async () => {
    if (!deleteStepTarget) return;
    await dispatch(deleteLIStep(campaignId, deleteStepTarget.id));
    setDeleteStepTarget(null);
  };

  // ── Add leads handlers ──
  const handleToggleLead = (leadId) => {
    setSelectedLeadIds((prev) =>
      prev.includes(leadId)
        ? prev.filter((id) => id !== leadId)
        : [...prev, leadId]
    );
  };

  const handleAddLeads = async () => {
    if (selectedLeadIds.length === 0) return;
    setAddingLeads(true);
    try {
      await dispatch(addLeadsToLICampaign(campaignId, selectedLeadIds));
      setAddLeadsOpen(false);
      setSelectedLeadIds([]);
      dispatch(fetchLICampaignLeads(campaignId, { page: leadsPage + 1 }));
      dispatch(fetchLICampaignStats(campaignId));
    } finally {
      setAddingLeads(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatRate = (value) => {
    // Backend already returns rates as percentages (e.g. 11.6 for 11.6%).
    // Multiplying by 100 again was the cause of "1160.0%" displays.
    if (value === null || value === undefined) return "0%";
    return `${Number(value).toFixed(1)}%`;
  };

  const headerCellSx = {
    fontWeight: 600,
    fontSize: "0.8rem",
    bgcolor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5",
  };

  if (!campaign && loading) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", overflow: "auto" }}>
      {/* Top bar */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
        <IconButton
          size="small"
          onClick={() => navigate("/admin/linkedin-outreach/campaigns")}
          sx={{ mr: 0.5 }}
        >
          <ArrowLeft size={18} />
        </IconButton>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1rem" }}>
          {campaign?.name || "Campaign"}
        </Typography>
        {campaign?.status && (
          <Chip
            label={campaign.status}
            size="small"
            color={STATUS_COLORS[campaign.status] || "default"}
            sx={{
              borderRadius: "6px",
              fontSize: "0.7rem",
              fontWeight: 600,
              textTransform: "capitalize",
            }}
          />
        )}
      </Box>

      {/* Stats cards */}
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          mb: 2.5,
          flexWrap: "wrap",
        }}
      >
        <StatCard
          icon={Users}
          label="Total Leads"
          value={stats?.total_leads ?? 0}
          color="#3b82f6"
        />
        <StatCard
          icon={UserCheck}
          label="Connected"
          value={stats?.connected ?? 0}
          color="#10b981"
        />
        <StatCard
          icon={Reply}
          label="Replied"
          value={stats?.replied ?? 0}
          color="#8b5cf6"
        />
        <StatCard
          icon={XCircle}
          label="Failed"
          value={stats?.failed ?? 0}
          color="#ef4444"
        />
        <StatCard
          icon={TrendingUp}
          label="Conn. Rate"
          value={formatRate(stats?.connection_rate)}
          color="#f59e0b"
        />
        <StatCard
          icon={Percent}
          label="Reply Rate"
          value={formatRate(stats?.reply_rate)}
          color="#06b6d4"
        />
      </Box>

      {/* Per-step action stats */}
      <ActionStepStats stepStats={stats?.step_stats || []} />

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{
            minHeight: 36,
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.85rem",
              minHeight: 36,
              py: 0,
            },
          }}
        >
          <Tab label="Sequence" />
          <Tab label="Leads" />
        </Tabs>
      </Box>

      {/* ────────── Sequence Tab ────────── */}
      {activeTab === 0 && (
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: theme.palette.text.secondary }}
            >
              {sortedSteps.length} step{sortedSteps.length !== 1 ? "s" : ""}
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<Plus size={16} />}
              onClick={handleOpenStepCreate}
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              Add Step
            </Button>
          </Box>

          {/* Timeline */}
          {sortedSteps.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                p: 4,
                borderRadius: "12px",
                border: `1px solid ${theme.palette.divider}`,
                textAlign: "center",
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.secondary }}
              >
                No steps yet. Add your first step to build the sequence.
              </Typography>
            </Paper>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
              {sortedSteps.map((step, idx) => {
                const config = getActionConfig(step.action_type);
                const StepIcon = config.icon;
                const isLast = idx === sortedSteps.length - 1;
                return (
                  <Box key={step.id} sx={{ display: "flex", gap: 2 }}>
                    {/* Timeline line + dot */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        width: 40,
                        flexShrink: 0,
                      }}
                    >
                      {/* Top connector */}
                      <Box
                        sx={{
                          width: 2,
                          flex: 1,
                          bgcolor:
                            idx === 0 ? "transparent" : theme.palette.divider,
                          minHeight: 8,
                        }}
                      />
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "8px",
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <StepIcon
                          size={16}
                          color={theme.palette.primary.main}
                        />
                      </Box>
                      {/* Bottom connector */}
                      <Box
                        sx={{
                          width: 2,
                          flex: 1,
                          bgcolor: isLast
                            ? "transparent"
                            : theme.palette.divider,
                          minHeight: 8,
                        }}
                      />
                    </Box>

                    {/* Step card */}
                    <Paper
                      elevation={0}
                      sx={{
                        flex: 1,
                        p: 2,
                        mb: isLast ? 0 : 1,
                        borderRadius: "10px",
                        border: `1px solid ${theme.palette.divider}`,
                        transition: "border-color 0.2s",
                        "&:hover": {
                          borderColor: alpha(theme.palette.primary.main, 0.3),
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                        }}
                      >
                        <Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mb: 0.5,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600 }}
                            >
                              Step {step.step_order}: {config.label}
                            </Typography>
                            {step.delay_hours > 0 && (
                              <Chip
                                icon={<Clock size={12} />}
                                label={`${step.delay_hours}h delay`}
                                size="small"
                                variant="outlined"
                                sx={{
                                  borderRadius: "6px",
                                  fontSize: "0.65rem",
                                  height: 22,
                                }}
                              />
                            )}
                          </Box>
                          {step.connection_note && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: theme.palette.text.secondary,
                                display: "block",
                                mt: 0.5,
                                maxWidth: 500,
                              }}
                            >
                              Note: {step.connection_note}
                            </Typography>
                          )}
                          {step.message_template && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: theme.palette.text.secondary,
                                display: "block",
                                mt: 0.5,
                                maxWidth: 500,
                                whiteSpace: "pre-wrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: "vertical",
                                display: "-webkit-box",
                              }}
                            >
                              {step.message_template}
                            </Typography>
                          )}
                          {step.condition_type && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                mt: 0.75,
                              }}
                            >
                              <GitBranch
                                size={12}
                                color={theme.palette.warning.main}
                              />
                              <Typography
                                variant="caption"
                                sx={{ color: theme.palette.warning.main, fontWeight: 500 }}
                              >
                                {step.condition_type.replace(/_/g, " ")}
                                {step.condition_timeout_hours
                                  ? ` (${step.condition_timeout_hours}h)`
                                  : ""}
                                {step.on_true_goto_step != null &&
                                  ` | true -> step ${step.on_true_goto_step}`}
                                {step.on_false_goto_step != null &&
                                  ` | false -> step ${step.on_false_goto_step}`}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenStepEdit(step)}
                            >
                              <PenLine size={14} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => setDeleteStepTarget(step)}
                            >
                              <Trash2
                                size={14}
                                color={theme.palette.error?.main}
                              />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    </Paper>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      )}

      {/* ────────── Leads Tab ────────── */}
      {activeTab === 1 && (
        <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: theme.palette.text.secondary }}
            >
              {leadTotal} lead{leadTotal !== 1 ? "s" : ""}
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<Plus size={16} />}
              onClick={() => setAddLeadsOpen(true)}
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              Add Leads
            </Button>
          </Box>

          <Paper
            elevation={0}
            sx={{
              borderRadius: "12px",
              border: `1px solid ${theme.palette.divider}`,
              overflow: "hidden",
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <TableContainer sx={{ flex: 1, overflow: "auto" }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>Lead</TableCell>
                    <TableCell sx={headerCellSx}>Company</TableCell>
                    <TableCell sx={headerCellSx}>Account</TableCell>
                    <TableCell sx={headerCellSx}>Status</TableCell>
                    <TableCell sx={headerCellSx}>Current Step</TableCell>
                    <TableCell sx={headerCellSx}>Last Action</TableCell>
                    <TableCell sx={headerCellSx}>Next Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {leadItems.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        align="center"
                        sx={{ py: 4, color: theme.palette.text.secondary }}
                      >
                        No leads in this campaign yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    leadItems.map((cl) => (
                      <TableRow key={cl.id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {cl.lead?.full_name || cl.lead_name || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="caption"
                            sx={{ color: theme.palette.text.secondary }}
                          >
                            {cl.lead?.company || cl.company || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="caption"
                            sx={{ color: theme.palette.text.secondary }}
                          >
                            {cl.account?.email || cl.account_email || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={cl.status || "pending"}
                            size="small"
                            color={
                              LEAD_STATUS_COLORS[cl.status] || "default"
                            }
                            sx={{
                              borderRadius: "6px",
                              fontSize: "0.7rem",
                              fontWeight: 600,
                              textTransform: "capitalize",
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          {cl.current_step != null
                            ? `Step ${cl.current_step}`
                            : "-"}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                            {formatDate(cl.last_action_at)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                            {formatDate(cl.next_action_at)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {leadTotal > 25 && (
              <TablePagination
                component="div"
                count={leadTotal}
                page={leadsPage}
                onPageChange={(_, p) => setLeadsPage(p)}
                rowsPerPage={25}
                rowsPerPageOptions={[25]}
              />
            )}
          </Paper>
        </Box>
      )}

      {/* ────────── Step Dialog ────────── */}
      <Dialog
        open={stepDialogOpen}
        onClose={handleCloseStepDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          {editingStep ? "Edit Step" : "Add Step"}
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
          >
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="Step Order"
                type="number"
                value={stepForm.step_order}
                onChange={(e) =>
                  setStepForm({
                    ...stepForm,
                    step_order: parseInt(e.target.value) || 1,
                  })
                }
                size="small"
                sx={{ width: 120 }}
                inputProps={{ min: 1 }}
              />
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel>Action Type</InputLabel>
                <Select
                  value={stepForm.action_type}
                  onChange={(e) =>
                    setStepForm({ ...stepForm, action_type: e.target.value })
                  }
                  label="Action Type"
                >
                  {ACTION_TYPES.map((at) => (
                    <MenuItem key={at.value} value={at.value}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <at.icon size={14} />
                        {at.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="Delay (hours)"
                type="number"
                value={stepForm.delay_hours}
                onChange={(e) =>
                  setStepForm({
                    ...stepForm,
                    delay_hours: parseInt(e.target.value) || 0,
                  })
                }
                size="small"
                sx={{ flex: 1 }}
                inputProps={{ min: 0 }}
              />
              <TextField
                label="Jitter %"
                type="number"
                value={stepForm.delay_jitter_pct}
                onChange={(e) =>
                  setStepForm({
                    ...stepForm,
                    delay_jitter_pct: parseInt(e.target.value) || 0,
                  })
                }
                size="small"
                sx={{ flex: 1 }}
                inputProps={{ min: 0, max: 100 }}
                helperText="Randomize delay by this %"
              />
            </Box>

            {stepForm.action_type === "connect" && (
              <TextField
                label="Connection Note"
                value={stepForm.connection_note}
                onChange={(e) => {
                  if (e.target.value.length <= 300) {
                    setStepForm({
                      ...stepForm,
                      connection_note: e.target.value,
                    });
                  }
                }}
                fullWidth
                size="small"
                multiline
                rows={2}
                helperText={`${stepForm.connection_note.length}/300 characters. Merge fields: {{first_name}}, {{company}}, {{job_title}}`}
                inputProps={{ maxLength: 300 }}
              />
            )}

            {["message", "inmail"].includes(stepForm.action_type) && (
              <TextField
                label="Message Template"
                value={stepForm.message_template}
                onChange={(e) =>
                  setStepForm({
                    ...stepForm,
                    message_template: e.target.value,
                  })
                }
                fullWidth
                size="small"
                multiline
                rows={4}
                helperText="Merge fields: {{first_name}}, {{last_name}}, {{company}}, {{job_title}}, {{industry}}, {{location}}"
              />
            )}

            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                mt: 1,
                color: theme.palette.text.secondary,
              }}
            >
              Conditional Branching (optional)
            </Typography>

            <FormControl size="small" fullWidth>
              <InputLabel>Condition</InputLabel>
              <Select
                value={stepForm.condition_type}
                onChange={(e) =>
                  setStepForm({ ...stepForm, condition_type: e.target.value })
                }
                label="Condition"
              >
                {CONDITION_TYPES.map((ct) => (
                  <MenuItem key={ct.value} value={ct.value}>
                    {ct.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {stepForm.condition_type && (
              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField
                  label="Timeout (hours)"
                  type="number"
                  value={stepForm.condition_timeout_hours}
                  onChange={(e) =>
                    setStepForm({
                      ...stepForm,
                      condition_timeout_hours:
                        parseInt(e.target.value) || 24,
                    })
                  }
                  size="small"
                  sx={{ flex: 1 }}
                  inputProps={{ min: 1 }}
                />
                <TextField
                  label="True -> Step"
                  type="number"
                  value={stepForm.on_true_goto_step}
                  onChange={(e) =>
                    setStepForm({
                      ...stepForm,
                      on_true_goto_step:
                        e.target.value === ""
                          ? ""
                          : parseInt(e.target.value),
                    })
                  }
                  size="small"
                  sx={{ flex: 1 }}
                  inputProps={{ min: 1 }}
                />
                <TextField
                  label="False -> Step"
                  type="number"
                  value={stepForm.on_false_goto_step}
                  onChange={(e) =>
                    setStepForm({
                      ...stepForm,
                      on_false_goto_step:
                        e.target.value === ""
                          ? ""
                          : parseInt(e.target.value),
                    })
                  }
                  size="small"
                  sx={{ flex: 1 }}
                  inputProps={{ min: 1 }}
                />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseStepDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveStep}
            disabled={stepSubmitting}
            startIcon={
              stepSubmitting ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
          >
            {stepSubmitting
              ? "Saving..."
              : editingStep
              ? "Update Step"
              : "Add Step"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ────────── Add Leads Dialog ────────── */}
      <Dialog
        open={addLeadsOpen}
        onClose={() => {
          setAddLeadsOpen(false);
          setSelectedLeadIds([]);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Add Leads to Campaign</DialogTitle>
        <DialogContent>
          <Typography
            variant="caption"
            sx={{ color: theme.palette.text.secondary, mb: 1.5, display: "block" }}
          >
            Select validated leads to add to this campaign.
          </Typography>
          {availableLeads.length === 0 ? (
            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.secondary, py: 2, textAlign: "center" }}
            >
              No validated leads available.
            </Typography>
          ) : (
            <Box
              sx={{
                maxHeight: 360,
                overflow: "auto",
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: "8px",
              }}
            >
              {availableLeads.map((lead) => (
                <Box
                  key={lead.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.5,
                    py: 1,
                    cursor: "pointer",
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    "&:last-child": { borderBottom: "none" },
                    "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                    bgcolor: selectedLeadIds.includes(lead.id)
                      ? alpha(theme.palette.primary.main, 0.06)
                      : "transparent",
                  }}
                  onClick={() => handleToggleLead(lead.id)}
                >
                  <Checkbox
                    checked={selectedLeadIds.includes(lead.id)}
                    size="small"
                    disableRipple
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {lead.full_name || lead.first_name + " " + lead.last_name}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      {[lead.job_title, lead.company]
                        .filter(Boolean)
                        .join(" at ")}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setAddLeadsOpen(false);
              setSelectedLeadIds([]);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddLeads}
            disabled={selectedLeadIds.length === 0 || addingLeads}
            startIcon={
              addingLeads ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
          >
            {addingLeads
              ? "Adding..."
              : `Add ${selectedLeadIds.length} Lead${selectedLeadIds.length !== 1 ? "s" : ""}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ────────── Delete Step Confirm ────────── */}
      <Dialog
        open={!!deleteStepTarget}
        onClose={() => setDeleteStepTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Delete Step</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            Are you sure you want to delete{" "}
            <strong>
              Step {deleteStepTarget?.step_order}:{" "}
              {getActionConfig(deleteStepTarget?.action_type).label}
            </strong>
            ?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteStepTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteStep}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CampaignDetail;
